import * as THREE from 'three';
import { createNoise2D } from 'simplex-noise';

const TEX_BASE = '/assets/textures';

export interface TerrainOptions {
  size: number;       // meters on a side
  segments: number;   // grid resolution
  seed: number;
  /** Starting amplitude of the first fbm octave (metres). */
  amplitude?: number;
  /** Frequency / octave count / persistence for the fbm. Defaults kept in
   *  sync with the old hard-coded values so unconfigured terrain still
   *  looks the same. */
  frequency?: number;
  octaves?: number;
  persistence?: number;
  /** Surface material variant — affects future ground texture blending. */
  type?: 'grass' | 'dirt' | 'sand' | 'rock';
  /** Circular pit features cut into the terrain (nest entrances). */
  craters?: Array<{ x: number; z: number; radius: number; depth: number }>;
  /** Optional grayscale PNG URL. When set, its luminance drives terrain
   *  height — replacing the procedural fbm output entirely. The image is
   *  stretched to cover the whole playable square. */
  heightmapUrl?: string;
  /** Metres of vertical range the heightmap should span (white − black). */
  heightmapScale?: number;
  /** Optional surface-texture URL. When set, replaces the default grass/
   *  dirt slope-blended material with a plain diffuse map of the image. */
  textureUrl?: string;
  /** How many times the surface texture tiles across the world square. */
  textureTileRepeat?: number;
}

export class Terrain {
  opts: TerrainOptions;
  mesh!: THREE.Mesh;
  private noise: (x: number, y: number) => number;
  private heights!: Float32Array; // row-major (segments+1) x (segments+1)

  constructor(opts: TerrainOptions) {
    this.opts = opts;
    // Simple seeded PRNG → noise. simplex-noise takes a random func.
    const rng = mulberry32(opts.seed);
    this.noise = createNoise2D(rng);
  }

  /** World-space height query (used by grass, rocks, ants). */
  getHeight(x: number, z: number): number {
    return this.sampleHeight(x, z);
  }

  /** World-space surface normal (approx via finite differences). */
  getNormal(x: number, z: number, out = new THREE.Vector3()): THREE.Vector3 {
    const e = 0.2;
    const hL = this.sampleHeight(x - e, z);
    const hR = this.sampleHeight(x + e, z);
    const hD = this.sampleHeight(x, z - e);
    const hU = this.sampleHeight(x, z + e);
    out.set(hL - hR, 2 * e, hD - hU).normalize();
    return out;
  }

  /** Optional loaded heightmap. When present, sampleHeight reads from this
   *  instead of fbm. Stored as a float[] with one luminance per pixel
   *  in [0,1]. */
  private heightmap: { data: Float32Array; w: number; h: number } | null = null;

  async load() {
    const { size, segments } = this.opts;

    if (this.opts.heightmapUrl) {
      try { this.heightmap = await loadHeightmap(this.opts.heightmapUrl); }
      catch (err) { console.warn('[Terrain] heightmap load failed:', err); this.heightmap = null; }
    }

    const geo = new THREE.PlaneGeometry(size, size, segments, segments);
    geo.rotateX(-Math.PI / 2); // Y-up

    const pos = geo.attributes.position as THREE.BufferAttribute;
    const verts = pos.count;
    this.heights = new Float32Array(verts);
    for (let i = 0; i < verts; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      const h = this.sampleHeight(x, z);
      this.heights[i] = h;
      pos.setY(i, h);
    }
    pos.needsUpdate = true;
    geo.computeVertexNormals();

    // Custom surface-texture path: if the world config supplied a
    // textureUrl, load it as the sole diffuse map and skip the default
    // grass+dirt slope-blend shader. Tiles according to textureTileRepeat.
    let mat: THREE.MeshStandardMaterial;
    let customTexture: THREE.Texture | null = null;
    if (this.opts.textureUrl) {
      try { customTexture = await loadTexture(this.opts.textureUrl); }
      catch (err) { console.warn('[Terrain] surface texture load failed:', err); customTexture = null; }
    }
    if (customTexture) {
      const repeat = this.opts.textureTileRepeat ?? 1;
      customTexture.wrapS = customTexture.wrapT = repeat > 1 ? THREE.RepeatWrapping : THREE.ClampToEdgeWrapping;
      customTexture.repeat.set(repeat, repeat);
      customTexture.anisotropy = 8;
      mat = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        map: customTexture,
        roughness: 0.95,
        metalness: 0.0,
      });
      this.mesh = new THREE.Mesh(geo, mat);
      this.mesh.receiveShadow = true;
      // Terrain is too big to be a useful shadow caster — its silhouette
      // projects as a square onto distant geometry (the skirt hills),
      // creating an obvious "giant square shadow" artefact when the sun
      // is angled. Receive only.
      this.mesh.castShadow = false;
      this.mesh.name = 'terrain';
      return;
    }

    const [grass, dirt] = await Promise.all([
      loadSet(`${TEX_BASE}/aerial_grass_rock/aerial_grass_rock`),
      loadSet(`${TEX_BASE}/brown_mud_leaves_01/brown_mud_leaves_01`),
    ]);

    mat = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      map: grass.diffuse ?? null,
      normalMap: grass.normal ?? null,
      roughnessMap: grass.rough ?? null,
      roughness: 1.0,
      metalness: 0.0,
    });
    // Fallback color if no textures loaded.
    if (!grass.diffuse) mat.color.set(0x4a6b3a);

    mat.onBeforeCompile = (shader) => {
      shader.uniforms.uDirtMap = { value: dirt.diffuse ?? grass.diffuse };
      shader.uniforms.uDirtRough = { value: dirt.rough ?? grass.rough };
      shader.uniforms.uTileScale = { value: 0.25 };

      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>\nvarying vec3 vWorldPos;\nvarying vec3 vWorldNormal;`,
        )
        .replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>\nvWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;\nvWorldNormal = normalize(mat3(modelMatrix) * normal);`,
        );

      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          `#include <common>\nuniform sampler2D uDirtMap;\nuniform sampler2D uDirtRough;\nuniform float uTileScale;\nvarying vec3 vWorldPos;\nvarying vec3 vWorldNormal;\nfloat slopeBlend(){float s=1.0-clamp(vWorldNormal.y,0.0,1.0);return smoothstep(0.25,0.55,s);}`,
        )
        .replace(
          '#include <map_fragment>',
          `vec2 tUv = vWorldPos.xz * uTileScale;\nvec4 grassCol = texture2D(map, tUv);\nvec4 dirtCol = texture2D(uDirtMap, tUv);\nfloat blend = slopeBlend();\nvec4 sampledDiffuseColor = mix(grassCol, dirtCol, blend);\ndiffuseColor *= sampledDiffuseColor;`,
        )
        .replace(
          '#include <roughnessmap_fragment>',
          `float roughnessFactor = roughness;\n#ifdef USE_ROUGHNESSMAP\nvec4 grassR = texture2D(roughnessMap, vWorldPos.xz * uTileScale);\nvec4 dirtR = texture2D(uDirtRough, vWorldPos.xz * uTileScale);\nfloat sampledR = mix(grassR.g, dirtR.g, slopeBlend());\nroughnessFactor *= sampledR;\n#endif`,
        )
        .replace(
          '#include <normal_fragment_maps>',
          `#ifdef USE_NORMALMAP_TANGENTSPACE\nvec3 mapN = texture2D( normalMap, vWorldPos.xz * uTileScale ).xyz * 2.0 - 1.0;\nmapN.xy *= normalScale;\nnormal = normalize( tbn * mapN );\n#endif`,
        );
    };

    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.receiveShadow = true;
    this.mesh.castShadow = true;
    this.mesh.name = 'terrain';
  }

  // Fractal Brownian motion — multi-octave simplex, minus any crater pits.
  private fbm(x: number, z: number): number {
    let amp = this.opts.amplitude ?? 0.5;
    let freq = this.opts.frequency ?? 0.04;
    const octaves = this.opts.octaves ?? 5;
    const persistence = this.opts.persistence ?? 0.5;
    let sum = 0;
    for (let o = 0; o < octaves; o++) {
      sum += amp * this.noise(x * freq, z * freq);
      amp *= persistence;
      freq *= 2.05;
    }
    // Gentle dish bias so edges fall off slightly, centre is playable.
    const r = Math.hypot(x, z) / (this.opts.size * 0.5);
    sum -= 0.6 * Math.max(0, r - 0.7);
    return sum;
  }

  /** Bilinearly sample the loaded heightmap at world (x, z). Maps the full
   *  world square to the image's [0..w/h] bounds. */
  private heightmapAt(x: number, z: number): number {
    if (!this.heightmap) return 0;
    const half = this.opts.size * 0.5;
    const u = (x + half) / this.opts.size;          // [0,1]
    const v = (z + half) / this.opts.size;
    if (u < 0 || u > 1 || v < 0 || v > 1) return 0;
    const { data, w, h } = this.heightmap;
    const fx = u * (w - 1);
    const fz = v * (h - 1);
    const x0 = Math.floor(fx), z0 = Math.floor(fz);
    const x1 = Math.min(w - 1, x0 + 1), z1 = Math.min(h - 1, z0 + 1);
    const tx = fx - x0, tz = fz - z0;
    const c00 = data[z0 * w + x0];
    const c10 = data[z0 * w + x1];
    const c01 = data[z1 * w + x0];
    const c11 = data[z1 * w + x1];
    const a = c00 * (1 - tx) + c10 * tx;
    const b = c01 * (1 - tx) + c11 * tx;
    const lum = a * (1 - tz) + b * tz;         // [0,1]
    const scale = this.opts.heightmapScale ?? 2;
    return (lum - 0.5) * scale;
  }

  private sampleHeight(x: number, z: number): number {
    let h = this.heightmap ? this.heightmapAt(x, z) : this.fbm(x, z);
    // Craters are subtracted regardless of source so nest entrances are
    // always usable.
    if (this.opts.craters) {
      for (const c of this.opts.craters) {
        const dx = x - c.x;
        const dz = z - c.z;
        const d = Math.hypot(dx, dz);
        if (d < c.radius) {
          const t = d / c.radius;
          h -= c.depth * (1 - t) * (1 - t);
        }
      }
    }
    return h;
  }
}

function loadTexture(url: string): Promise<THREE.Texture> {
  return new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(
      url,
      (t) => { t.colorSpace = THREE.SRGBColorSpace; resolve(t); },
      undefined,
      () => reject(new Error('texture load failed: ' + url)),
    );
  });
}

async function loadHeightmap(url: string): Promise<{ data: Float32Array; w: number; h: number }> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('heightmap image load error: ' + url));
    i.src = url;
  });
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');
  ctx.drawImage(img, 0, 0);
  const { data: rgba } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = new Float32Array(canvas.width * canvas.height);
  for (let i = 0; i < data.length; i++) {
    // Luminance = average of the RGB channels, rescaled to [0,1].
    const r = rgba[i * 4];
    const g = rgba[i * 4 + 1];
    const b = rgba[i * 4 + 2];
    data[i] = (r + g + b) / (3 * 255);
  }
  return { data, w: canvas.width, h: canvas.height };
}

interface TextureSet {
  diffuse?: THREE.Texture;
  normal?: THREE.Texture;
  rough?: THREE.Texture;
}

async function loadSet(prefix: string, res = '2k'): Promise<TextureSet> {
  const loader = new THREE.TextureLoader();
  const tryLoad = (url: string) =>
    new Promise<THREE.Texture | undefined>((resolve) => {
      loader.load(
        url,
        (t) => {
          t.wrapS = t.wrapT = THREE.RepeatWrapping;
          t.anisotropy = 8;
          resolve(t);
        },
        undefined,
        () => resolve(undefined),
      );
    });

  const [diffuse, normal, rough] = await Promise.all([
    tryLoad(`${prefix}_diffuse_${res}.jpg`),
    tryLoad(`${prefix}_nor_gl_${res}.jpg`),
    tryLoad(`${prefix}_rough_${res}.jpg`),
  ]);
  if (diffuse) diffuse.colorSpace = THREE.SRGBColorSpace;
  return { diffuse, normal, rough };
}

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
